import { supabase } from '@/lib/supabase';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import { isNwsisdMemberDistrictNumber, normalizeDistrictNumber } from '@/utils/district/geospatialService';

export interface BoundaryLookupResult {
  districtId: string;
  districtName: string;
  boundarySchools?: string[];
  isInNWSISD: boolean;
  coordinates?: {
    lat: number;
    lng: number;
  };
  confidence?: 'high' | 'medium' | 'low';
}

/**
 * Service to determine which school district boundary an address falls into
 * Uses Google Maps geocoding + PostGIS boundary checking with caching
 */
export class BoundaryLookupService {
  private static instance: BoundaryLookupService;
  private geocoder: google.maps.Geocoder | null = null;
  
  // Cache for boundary lookup results
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private boundaryCache = new Map<string, {
    result: BoundaryLookupResult;
    timestamp: number;
    grade?: string;
  }>();
  
  // Track recently cleared cache keys to prevent immediate re-caching
  private recentlyCleared = new Map<string, number>();
  private static readonly CLEARED_IGNORE_DURATION = 5000; // 5 seconds
  
  // Cache duration: 30 days for boundary results
  private static readonly CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days
  
  // Queue for batch processing
  private batchQueue: Array<{
    address: string;
    grade?: string;
    resolve: (result: BoundaryLookupResult | null) => void;
    reject: (error: Error) => void;
  }> = [];
  
  private batchTimer: NodeJS.Timeout | null = null;
  private static readonly BATCH_DELAY = 500; // 500ms delay for batching
  private static readonly BATCH_SIZE = 10; // Process 10 addresses at once
  
  // Request deduplication - prevent multiple API calls for same address
  private pendingRequests = new Map<string, Promise<BoundaryLookupResult | null>>();

  private constructor() {}

  public static getInstance(): BoundaryLookupService {
    if (!BoundaryLookupService.instance) {
      BoundaryLookupService.instance = new BoundaryLookupService();
    }
    return BoundaryLookupService.instance;
  }

  private async initializeGeocoder(): Promise<void> {
    if (!this.geocoder && window.google?.maps) {
      this.geocoder = new google.maps.Geocoder();
    }
  }

  /**
   * Get school district assignment for a given address with caching and deduplication
   */
  async getDistrictForAddress(address: string, studentGrade?: string): Promise<BoundaryLookupResult | null> {
    // Create cache key that includes both address and grade
    const cacheKey = this.createCacheKey(address, studentGrade);
    
    // Check cache first
    const cachedResult = await this.getCachedResult(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }
    
    // Check if there's already a pending request for this address/grade combination
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }
    
    // Create new request and store it to prevent duplicates
    const requestPromise = this.performDistrictLookup(address, studentGrade, cacheKey);
    this.pendingRequests.set(cacheKey, requestPromise);
    
    // Clean up pending request when done
    requestPromise.finally(() => {
      this.pendingRequests.delete(cacheKey);
    });
    
    return requestPromise;
  }

  /**
   * Perform the actual district lookup (extracted to separate method for deduplication)
   */
  private async performDistrictLookup(address: string, studentGrade?: string, cacheKey: string): Promise<BoundaryLookupResult | null> {
    
    try {
      // First geocode the address to get coordinates
      const coordinates = await this.geocodeAddress(address);
      if (!coordinates) {
        return null;
      }


      // TODO: Use Google Maps Data-Driven Styling SCHOOL_DISTRICT boundary API
      // For now, using PostGIS as fallback
      const district = await this.lookupDistrictByCoordinates(coordinates.lat, coordinates.lng);
      
      if (district) {
        
        // Get boundary schools filtered by student's grade level
        const boundarySchools = await this.lookupBoundarySchools(coordinates.lat, coordinates.lng, district.districtName, studentGrade);
        
        const result: BoundaryLookupResult = {
          ...district,
          boundarySchools,
          coordinates,
          confidence: 'high'
        };
        
        // Cache the result
        await this.setCachedResult(cacheKey, result, studentGrade, address);
        
        return result;
      }

      return null;
    } catch (error) {
      console.error('Error in performDistrictLookup:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'perform district lookup' });
      return null;
    }
  }

  /**
   * Create cache key from address and grade
   */
  private createCacheKey(address: string, grade?: string): string {
    // Normalize address for consistent caching
    const normalizedAddress = address.toLowerCase().trim().replace(/\s+/g, ' ');
    return `${normalizedAddress}|${grade || 'no-grade'}`;
  }

  /**
   * Get cached result if still valid (checks both memory and database)
   */
  private async getCachedResult(cacheKey: string): Promise<BoundaryLookupResult | null> {
    
    // Check if this key was recently cleared - if so, ignore cache for a short time
    const clearedTime = this.recentlyCleared.get(cacheKey);
    if (clearedTime && Date.now() - clearedTime < BoundaryLookupService.CLEARED_IGNORE_DURATION) {
      const remainingMs = BoundaryLookupService.CLEARED_IGNORE_DURATION - (Date.now() - clearedTime);
      return null;
    }
    
    // First check memory cache
    const memoryCache = this.boundaryCache.get(cacheKey);
    if (memoryCache) {
      const now = Date.now();
      if (now - memoryCache.timestamp <= BoundaryLookupService.CACHE_DURATION) {
        const ageHours = Math.round((now - memoryCache.timestamp) / (1000 * 60 * 60));
        return memoryCache.result;
      } else {
        this.boundaryCache.delete(cacheKey);
      }
    }
    
    // Check database cache
    try {
      const { data, error } = await supabase
        .from('boundary_cache')
        .select('*')
        .eq('cache_key', cacheKey)
        .gt('expires_at', new Date().toISOString())
        .single();
      
      if (error) {
        return null;
      }
      
      if (!data) {
        return null;
      }
      
      const ageDays = Math.round((Date.now() - new Date(data.created_at).getTime()) / (1000 * 60 * 60 * 24));
      
      // Reconstruct BoundaryLookupResult from database
      const result: BoundaryLookupResult = {
        districtId: data.district_id,
        districtName: data.district_name,
        boundarySchools: data.boundary_schools,
        isInNWSISD: data.is_in_nwsisd,
        coordinates: data.coordinates,
        confidence: data.confidence as 'high' | 'medium' | 'low'
      };
      
      // Store in memory cache for faster subsequent access
      this.boundaryCache.set(cacheKey, {
        result,
        timestamp: new Date(data.created_at).getTime(),
        grade: data.student_grade
      });
      
      return result;
      
    } catch (error) {
      console.error('Error checking database cache:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'check database cache' });
      return null;
    }
  }

  /**
   * Set cached result (saves to both memory and database)
   */
  private async setCachedResult(cacheKey: string, result: BoundaryLookupResult, grade?: string, address?: string): Promise<void> {
    const now = Date.now();
    
    // Save to memory cache
    this.boundaryCache.set(cacheKey, {
      result,
      timestamp: now,
      grade
    });
    
    // Save to database cache
    try {
      const expiresAt = new Date(now + BoundaryLookupService.CACHE_DURATION);
      
      const { error } = await supabase
        .from('boundary_cache')
        .upsert({
          cache_key: cacheKey,
          address: address || cacheKey.split('|')[0], // Extract address from cache key
          student_grade: grade,
          district_id: result.districtId,
          district_name: result.districtName,
          boundary_schools: result.boundarySchools || [],
          coordinates: result.coordinates,
          confidence: result.confidence || 'high',
          is_in_nwsisd: result.isInNWSISD,
          expires_at: expiresAt.toISOString(),
          updated_at: new Date().toISOString()
        });
      
      if (error) {
        console.error('Error saving to database cache:', error);
        EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'save database cache' });
      } else {
      }
      
    } catch (error) {
      console.error('Error saving cached result to database:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'save database cache' });
    }
  }

  /**
   * Clear specific cache entry for an address/grade combination
   */
  public async clearCacheForAddress(address: string, grade?: string): Promise<void> {
    const cacheKey = this.createCacheKey(address, grade);
    const normalizedAddress = address.toLowerCase().trim().replace(/\s+/g, ' ');
    
    
    // Clear from memory cache - also clear any variations of this address
    let memoryClearCount = 0;
    const keysToDelete: string[] = [];
    for (const [key, cached] of this.boundaryCache.entries()) {
      if (key.startsWith(normalizedAddress + '|')) {
        keysToDelete.push(key);
        memoryClearCount++;
      }
    }
    keysToDelete.forEach(key => this.boundaryCache.delete(key));
    
    if (memoryClearCount > 0) {
    }
    
    // Clear from database cache - clear all entries for this address
    try {
      // Check for entries by both cache_key pattern and address match
      const [{ data: keyMatches }, { data: addressMatches }] = await Promise.all([
        supabase
          .from('boundary_cache')
          .select('cache_key, address, student_grade')
          .ilike('cache_key', `${normalizedAddress}|%`),
        supabase
          .from('boundary_cache')
          .select('cache_key, address, student_grade')
          .ilike('address', `%${normalizedAddress}%`)
      ]);
      
      // Combine and deduplicate entries
      const allEntries = [...(keyMatches || []), ...(addressMatches || [])];
      const uniqueEntries = allEntries.filter((entry, index, arr) => 
        arr.findIndex(e => e.cache_key === entry.cache_key) === index
      );
      
      if (uniqueEntries && uniqueEntries.length > 0) {
        uniqueEntries.forEach(entry => {
        });
        
        // Delete each cache key individually with detailed logging
        const deletePromises = uniqueEntries.map(async (entry) => {
          const result = await supabase
            .from('boundary_cache')
            .delete()
            .eq('cache_key', entry.cache_key);
          
          if (result.error) {
            console.error(`   ❌ Failed to delete ${entry.cache_key}:`, result.error);
          } else {
          }
          return result;
        });
        
        const results = await Promise.all(deletePromises);
        const deletedCount = results.filter(result => !result.error).length;
        
        if (deletedCount !== uniqueEntries.length) {
          const failedCount = uniqueEntries.length - deletedCount;
        }
        
        // Mark the key as recently cleared to ignore cache hits for a short time
        this.recentlyCleared.set(cacheKey, Date.now());
        
        // Clean up old cleared entries
        this.cleanupRecentlyCleared();
        
      } else {
        // Still mark as cleared in case there are stale entries we didn't find
        this.recentlyCleared.set(cacheKey, Date.now());
        this.cleanupRecentlyCleared();
      }
    } catch (error) {
      console.error('Error clearing cache from database:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'clear cache' });
    }
  }

  /**
   * Clean up old recently cleared entries
   */
  private cleanupRecentlyCleared(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    for (const [key, timestamp] of this.recentlyCleared.entries()) {
      if (now - timestamp > BoundaryLookupService.CLEARED_IGNORE_DURATION) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.recentlyCleared.delete(key));
    
    if (keysToDelete.length > 0) {
    }
  }

  /**
   * Clear expired cache entries (both memory and database)
   */
  public async clearExpiredCache(): Promise<void> {
    const now = Date.now();
    
    // Clear expired memory cache entries
    const keysToDelete: string[] = [];
    for (const [key, cached] of this.boundaryCache.entries()) {
      if (now - cached.timestamp > BoundaryLookupService.CACHE_DURATION) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.boundaryCache.delete(key));
    
    // Clear expired database cache entries. The function runs as the caller and the
    // table's delete policy is for signed-in users only, so for an anonymous visitor
    // (family form, tracking page) the hourly call could only ever be a no-op: skip it.
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return;
      const { data, error } = await supabase.rpc('cleanup_expired_boundary_cache');
      
      if (error) {
        console.error('Error cleaning database cache:', error);
        EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'clean database cache' });
      } else {
      }
    } catch (error) {
      console.error('Error calling cleanup function:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'cleanup function' });
    }
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): { size: number; oldestEntry: number; newestEntry: number } {
    if (this.boundaryCache.size === 0) {
      return { size: 0, oldestEntry: 0, newestEntry: 0 };
    }
    
    let oldest = Date.now();
    let newest = 0;
    
    for (const cached of this.boundaryCache.values()) {
      oldest = Math.min(oldest, cached.timestamp);
      newest = Math.max(newest, cached.timestamp);
    }
    
    return {
      size: this.boundaryCache.size,
      oldestEntry: oldest,
      newestEntry: newest
    };
  }

  /**
   * Lookup boundary schools using district-specific APIs
   */
  private async lookupBoundarySchools(lat: number, lng: number, districtName: string, studentGrade?: string): Promise<string[]> {
    
    try {
      if (districtName.includes('Anoka-Hennepin')) {
        return await this.lookupAnokaHennepinSchools(lat, lng, studentGrade);
      } else if (districtName.includes('Osseo')) {
        return await this.lookupOsseoSchools(lat, lng, studentGrade);
      } else if (districtName.includes('Brooklyn Center')) {
        return await this.lookupBrooklynCenterSchools(lat, lng, studentGrade);
      } else if (districtName.includes('Fridley')) {
        return await this.lookupFridleySchools(lat, lng, studentGrade);
      } else if (districtName.includes('Elk River') || districtName.includes('ISD728')) {
        return await this.lookupElkRiverSchools(lat, lng, studentGrade);
      } else if (districtName.includes('Buffalo') || districtName.includes('Hanover') || districtName.includes('Montrose')) {
        return await this.lookupBHMSchools(lat, lng, studentGrade);
      } else if (districtName.includes('Rockford')) {
        return await this.lookupRockfordSchools(lat, lng, studentGrade);
      }
      
      return await this.lookupSchoolsWithGooglePlaces(lat, lng, districtName, studentGrade);
      
    } catch (error) {
      console.error('Error looking up boundary schools:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'lookup boundary schools' });
      return ['❌ Boundary lookup failed'];
    }
  }

  /**
   * Lookup schools for Anoka-Hennepin district using Google Places API
   */
  private async lookupAnokaHennepinSchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    
    try {
      // Get detailed location information for context
      const locationDetails = await this.reverseGeocode(lat, lng);
      const formattedAddress = locationDetails.formatted_address;
      
      // Use Google Places API to find nearby schools of each type
      const schoolsByType = await this.findNearbySchoolsByType(lat, lng);
      
      // Determine which school level to show based on student's applying grade
      const schoolLevel = this.determineSchoolLevelFromGrade(studentGrade);
      
      // Format results based on student's grade level
      const results: string[] = [];
      
      switch (schoolLevel) {
        case 'elementary':
          if (schoolsByType.elementary.length > 0) {
            results.push(...schoolsByType.elementary);
          } else {
            results.push('No elementary schools found nearby');
          }
          break;
          
        case 'middle':
          if (schoolsByType.middle.length > 0) {
            results.push(...schoolsByType.middle);
          } else {
            results.push('No middle schools found nearby');
          }
          break;
          
        case 'high':
          if (schoolsByType.high.length > 0) {
            results.push(...schoolsByType.high);
          } else {
            results.push('No high schools found nearby');
          }
          break;
          
        default:
          // If grade is unclear, show all levels with labels
          if (schoolsByType.elementary.length > 0) {
            results.push(`Elementary: ${schoolsByType.elementary.join(', ')}`);
          }
          if (schoolsByType.middle.length > 0) {
            results.push(`Middle: ${schoolsByType.middle.join(', ')}`);
          }
          if (schoolsByType.high.length > 0) {
            results.push(`High: ${schoolsByType.high.join(', ')}`);
          }
          break;
      }
      
      
      return results;
      
    } catch (error) {
      console.error('Error in Anoka-Hennepin lookup:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'anoka hennepin lookup' });
      return ['❌ School lookup failed'];
    }
  }

  /**
   * Determine school level based on student's applying grade
   */
  private determineSchoolLevelFromGrade(grade?: string): 'elementary' | 'middle' | 'high' | 'unknown' {
    if (!grade) return 'unknown';
    
    const gradeStr = grade.toLowerCase();
    
    // Elementary grades (K-5)
    if (gradeStr.includes('k') || gradeStr.includes('kindergarten') ||
        gradeStr === '1' || gradeStr === '2' || gradeStr === '3' || 
        gradeStr === '4' || gradeStr === '5' ||
        gradeStr === 'grade 1' || gradeStr === 'grade 2' || gradeStr === 'grade 3' ||
        gradeStr === 'grade 4' || gradeStr === 'grade 5') {
      return 'elementary';
    }
    
    // Middle school grades (6-8)
    if (gradeStr === '6' || gradeStr === '7' || gradeStr === '8' ||
        gradeStr === 'grade 6' || gradeStr === 'grade 7' || gradeStr === 'grade 8') {
      return 'middle';
    }
    
    // High school grades (9-12)
    if (gradeStr === '9' || gradeStr === '10' || gradeStr === '11' || gradeStr === '12' ||
        gradeStr === 'grade 9' || gradeStr === 'grade 10' || gradeStr === 'grade 11' || gradeStr === 'grade 12') {
      return 'high';
    }
    
    return 'unknown';
  }

  /**
   * Find nearby schools by type using Google Places API
   */
  private async findNearbySchoolsByType(lat: number, lng: number): Promise<{
    elementary: string[],
    middle: string[],
    high: string[]
  }> {
    
    if (!window.google?.maps?.places) {
      console.error('Google Places API not available');
      return {
        elementary: ['❌ Google Places API not loaded'],
        middle: ['❌ Google Places API not loaded'],
        high: ['❌ Google Places API not loaded']
      };
    }
    
    try {
      // Create a dummy map element for PlacesService (required by API)
      const mapDiv = document.createElement('div');
      const map = new google.maps.Map(mapDiv);
      const placesService = new google.maps.places.PlacesService(map);
      
      // Search for schools within 5km radius
      const request: google.maps.places.PlaceSearchRequest = {
        location: new google.maps.LatLng(lat, lng),
        radius: 5000, // 5km radius 
        type: 'school'
      };
      
      const schools = await this.performPlacesSearch(placesService, request);
      
      // Categorize schools by type based on name, sorted by distance
      const categorized = this.categorizeSchoolsByType(schools, lat, lng);
      
      
      return categorized;
      
    } catch (error) {
      console.error('Error searching for schools:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'search schools' });
      return {
        elementary: ['❌ School search failed'],
        middle: ['❌ School search failed'],
        high: ['❌ School search failed']
      };
    }
  }

  /**
   * Perform Places API search and return results as Promise
   */
  private performPlacesSearch(
    placesService: google.maps.places.PlacesService, 
    request: google.maps.places.PlaceSearchRequest
  ): Promise<google.maps.places.PlaceResult[]> {
    return new Promise((resolve, reject) => {
      placesService.nearbySearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          resolve(results);
        } else {
          reject(new Error(`Places search failed: ${status}`));
        }
      });
    });
  }

  /**
   * Categorize schools by type based on school names with sorting and enriched data
   */
  private categorizeSchoolsByType(schools: google.maps.places.PlaceResult[], targetLat?: number, targetLng?: number): {
    elementary: string[],
    middle: string[],
    high: string[]
  } {
    // Enrich schools with distance and rating data
    const enrichedSchools = schools.map(school => {
      let distance = 0;
      if (targetLat && targetLng && school.geometry?.location) {
        const schoolLat = school.geometry.location.lat();
        const schoolLng = school.geometry.location.lng();
        distance = this.calculateDistance(targetLat, targetLng, schoolLat, schoolLng);
      }
      
      return {
        name: school.name || 'Unknown School',
        distance,
        rating: school.rating || 0,
        userRatingsTotal: school.user_ratings_total || 0,
        level: this.determineSchoolLevel(school.name || '')
      };
    });

    // Sort by distance (closest first), then by rating (highest first)
    const sortedSchools = enrichedSchools.sort((a, b) => {
      // First sort by distance
      if (a.distance !== b.distance) {
        return a.distance - b.distance;
      }
      // Then by rating
      if (a.rating !== b.rating) {
        return b.rating - a.rating;
      }
      // Finally by number of ratings (more ratings = more established)
      return b.userRatingsTotal - a.userRatingsTotal;
    });

    // Categorize into levels - only take the closest school for each level
    const elementary: string[] = [];
    const middle: string[] = [];
    const high: string[] = [];
    
    sortedSchools.forEach(school => {
      switch (school.level) {
        case 'elementary':
          if (elementary.length === 0) { // Only add if no elementary school added yet
            elementary.push(school.name);
          }
          break;
        case 'middle':
          if (middle.length === 0) { // Only add if no middle school added yet
            middle.push(school.name);
          }
          break;
        case 'high':
          if (high.length === 0) { // Only add if no high school added yet
            high.push(school.name);
          }
          break;
        default:
          if (elementary.length === 0) { // Only add unclear schools if no elementary added yet
            elementary.push(`${school.name} (level unclear)`);
          }
      }
    });
    
    return { elementary, middle, high };
  }

  /**
   * Calculate distance between two coordinates in kilometers
   */
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = this.deg2rad(lat2 - lat1);
    const dLng = this.deg2rad(lng2 - lng1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI/180);
  }

  /**
   * Determine school level from name
   */
  private determineSchoolLevel(name: string): 'elementary' | 'middle' | 'high' | 'unknown' {
    const lowerName = name.toLowerCase();
    
    if (lowerName.includes('elementary') || lowerName.includes('primary') || lowerName.includes('early childhood')) {
      return 'elementary';
    }
    if (lowerName.includes('middle') || lowerName.includes('junior') || lowerName.includes('intermediate')) {
      return 'middle';
    }
    if (lowerName.includes('high') || lowerName.includes('senior')) {
      return 'high';
    }
    
    return 'unknown';
  }

  /**
   * Lookup schools for other districts (placeholder implementations)
   */
  private async lookupOsseoSchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    return await this.lookupSchoolsWithGooglePlaces(lat, lng, 'Osseo Area Schools', studentGrade);
  }

  private async lookupBrooklynCenterSchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    return await this.lookupSchoolsWithGooglePlaces(lat, lng, 'Brooklyn Center Community Schools', studentGrade);
  }

  private async lookupFridleySchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    return await this.lookupSchoolsWithGooglePlaces(lat, lng, 'Fridley Public Schools', studentGrade);
  }

  private async lookupElkRiverSchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    return await this.lookupSchoolsWithGooglePlaces(lat, lng, 'Elk River Area Schools', studentGrade);
  }

  private async lookupBHMSchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    return await this.lookupSchoolsWithGooglePlaces(lat, lng, 'Buffalo-Hanover-Montrose Schools', studentGrade);
  }

  private async lookupRockfordSchools(lat: number, lng: number, studentGrade?: string): Promise<string[]> {
    return await this.lookupSchoolsWithGooglePlaces(lat, lng, 'Rockford Area Schools', studentGrade);
  }

  /**
   * Generic lookup using Google Places API for any district
   */
  private async lookupSchoolsWithGooglePlaces(lat: number, lng: number, districtName: string, studentGrade?: string): Promise<string[]> {
    try {
      // Use the same Google Places logic as Anoka-Hennepin
      const schoolsByType = await this.findNearbySchoolsByType(lat, lng);
      
      // If student grade is provided, filter by school level like Anoka-Hennepin
      if (studentGrade) {
        const schoolLevel = this.determineSchoolLevelFromGrade(studentGrade);
        
        switch (schoolLevel) {
          case 'elementary':
            if (schoolsByType.elementary.length > 0) {
              return [schoolsByType.elementary[0]]; // Just the closest elementary
            }
            return [`No elementary schools found in ${districtName} area`];
            
          case 'middle':
            if (schoolsByType.middle.length > 0) {
              return [schoolsByType.middle[0]]; // Just the closest middle
            }
            return [`No middle schools found in ${districtName} area`];
            
          case 'high':
            if (schoolsByType.high.length > 0) {
              return [schoolsByType.high[0]]; // Just the closest high
            }
            return [`No high schools found in ${districtName} area`];
            
          default:
            // If grade is unclear, show closest school of each type with labels
            const results: string[] = [];
            if (schoolsByType.elementary.length > 0) {
              results.push(`Elementary: ${schoolsByType.elementary[0]}`);
            }
            if (schoolsByType.middle.length > 0) {
              results.push(`Middle: ${schoolsByType.middle[0]}`);
            }
            if (schoolsByType.high.length > 0) {
              results.push(`High: ${schoolsByType.high[0]}`);
            }
            
            if (results.length === 0) {
              return [`No schools found in ${districtName} area`];
            }
            return results;
        }
      }
      
      // No grade provided, return closest school of each type
      const results: string[] = [];
      
      if (schoolsByType.elementary.length > 0) {
        results.push(schoolsByType.elementary[0]); // Just the closest elementary
      }
      if (schoolsByType.middle.length > 0) {
        results.push(schoolsByType.middle[0]); // Just the closest middle
      }
      if (schoolsByType.high.length > 0) {
        results.push(schoolsByType.high[0]); // Just the closest high
      }
      
      if (results.length === 0) {
        return [`No schools found in ${districtName} area`];
      }
      
      return results;
      
    } catch (error) {
      console.error(`Error looking up schools in ${districtName}:`, error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'lookup schools in district' });
      return [`❌ ${districtName} lookup failed`];
    }
  }

  /**
   * Reverse geocode coordinates to get detailed location information
   */
  private async reverseGeocode(lat: number, lng: number): Promise<any> {
    await this.initializeGeocoder();
    
    if (!this.geocoder) {
      throw new Error('Google Maps geocoder not available');
    }

    return new Promise((resolve, reject) => {
      this.geocoder!.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          resolve(results[0]);
        } else {
          reject(new Error(`Reverse geocoding failed: ${status}`));
        }
      });
    });
  }

  /**
   * Geocode address to get latitude/longitude coordinates
   */
  private async geocodeAddress(address: string): Promise<{lat: number, lng: number} | null> {
    await this.initializeGeocoder();
    
    if (!this.geocoder) {
      throw new Error('Google Maps geocoder not available');
    }

    return new Promise((resolve) => {
      this.geocoder!.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng()
          });
        } else {
          console.warn('Geocoding failed for address:', address, status);
          resolve(null);
        }
      });
    });
  }

  /**
   * Use PostGIS to find which school district contains the given coordinates
   */
  private async lookupDistrictByCoordinates(lat: number, lng: number): Promise<{districtId: string, districtName: string, isInNWSISD: boolean} | null> {
    try {
      const { data, error } = await supabase.rpc('find_school_district', {
        lat,
        lon: lng
      });

      if (error) {
        console.error('PostGIS district lookup error:', error);
        EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'postgis district lookup' });
        return null;
      }

      if (data && data.length > 0) {
        const district = data[0];
        return {
          districtId: district.district_id,
          districtName: district.district_name,
          // Prefer the state district number: it is stable, whereas the name
          // has ~34 spellings in production. Fall back to the name only when
          // the number is missing or not in the expected form.
          isInNWSISD: normalizeDistrictNumber(district.district_id) !== null
            ? isNwsisdMemberDistrictNumber(district.district_id)
            : this.isNWSISDMember(district.district_name)
        };
      }

      return null;
    } catch (error) {
      console.error('Error in lookupDistrictByCoordinates:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'lookup district by coordinates' });
      return null;
    }
  }

  /**
   * Check if district is a member of NWSISD consortium.
   *
   * Prefer isNwsisdMemberDistrictNumber() with the state district number.
   * This name-based path exists only for callers that have a name and no
   * number, and it is best-effort by nature.
   *
   * The previous implementation compared against a hardcoded list of display
   * names with a bidirectional substring test, and got five of the seven
   * members wrong: the resolver returns MDE's names ("Osseo Public School
   * District"), the list held the consortium's ("Osseo Area Schools"), and
   * neither string contains the other. It also false-positived — a bare
   * "Rockford" reverse-matched "Rockford Area Schools". District names have
   * roughly 34 spellings across five vocabularies in this codebase, so any
   * name comparison has to normalise before it can mean anything.
   */
  private isNWSISDMember(districtName: string): boolean {
    if (!districtName) return false;

    // Strip everything that varies between vocabularies — punctuation, the
    // "#279" suffix, and the interchangeable trailing words — then compare
    // on the distinctive stem that survives in every spelling.
    const stem = districtName
      .toLowerCase()
      .replace(/#\s*\d+/g, ' ')
      .replace(/\b(public|community|area|independent)\b/g, ' ')
      .replace(/\b(school|schools|district|isd)\b/g, ' ')
      .replace(/[^a-z]+/g, ' ')
      .trim();

    const memberStems = [
      'anoka hennepin',
      'brooklyn center',
      'buffalo hanover montrose',
      'elk river',
      'fridley',
      'osseo',
      'rockford',
      // districtValidation.ts remaps every Elk River variant to the long
      // "ISD728 Elk River | Otsego | Rogers | Zimmerman" form, which survives
      // the stripping above as this stem. Without it the app's own canonical
      // spelling for a member district would read as non-member.
      'isd elk river otsego rogers zimmerman',
    ];

    // Exact stem match only. A substring test here is what produced the
    // original false positives.
    return memberStems.includes(stem);
  }

  /**
   * Enhanced batch lookup with intelligent queuing
   */
  async getDistrictForAddressBatched(address: string, studentGrade?: string): Promise<BoundaryLookupResult | null> {
    return new Promise((resolve, reject) => {
      // Add to batch queue
      this.batchQueue.push({
        address,
        grade: studentGrade,
        resolve,
        reject
      });

      // Process batch if queue is full
      if (this.batchQueue.length >= BoundaryLookupService.BATCH_SIZE) {
        this.processBatchQueue();
      } else {
        // Set timer to process batch after delay
        if (this.batchTimer) {
          clearTimeout(this.batchTimer);
        }
        this.batchTimer = setTimeout(() => {
          this.processBatchQueue();
        }, BoundaryLookupService.BATCH_DELAY);
      }
    });
  }

  /**
   * Process the current batch queue
   */
  private async processBatchQueue(): Promise<void> {
    if (this.batchQueue.length === 0) return;

    const currentBatch = this.batchQueue.splice(0, BoundaryLookupService.BATCH_SIZE);

    // Clear the timer
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Process all addresses in parallel
    const batchPromises = currentBatch.map(async (item) => {
      try {
        const result = await this.getDistrictForAddress(item.address, item.grade);
        item.resolve(result);
      } catch (error) {
        item.reject(error instanceof Error ? error : new Error('Unknown batch processing error'));
      }
    });

    await Promise.all(batchPromises);
  }

  /**
   * Batch lookup for multiple addresses (for filtering applications)
   */
  async batchLookupDistricts(addressData: Array<{address: string, grade?: string}>): Promise<Map<string, BoundaryLookupResult>> {
    const results = new Map<string, BoundaryLookupResult>();
    
    // Check cache first for all addresses
    const uncachedAddresses: Array<{address: string, grade?: string}> = [];
    const cacheHits: Array<{address: string, result: BoundaryLookupResult}> = [];
    
    for (const item of addressData) {
      const cacheKey = this.createCacheKey(item.address, item.grade);
      const cachedResult = this.getCachedResult(cacheKey);
      
      if (cachedResult) {
        cacheHits.push({ address: item.address, result: cachedResult });
        results.set(item.address, cachedResult);
      } else {
        uncachedAddresses.push(item);
      }
    }
    
    
    if (uncachedAddresses.length === 0) {
      return results;
    }
    
    // Process uncached addresses in batches
    const batchSize = BoundaryLookupService.BATCH_SIZE;
    for (let i = 0; i < uncachedAddresses.length; i += batchSize) {
      const batch = uncachedAddresses.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (item) => {
        try {
          const result = await this.getDistrictForAddress(item.address, item.grade);
          if (result) {
            results.set(item.address, result);
          }
        } catch (error) {
          console.error(`❌ Error processing address ${item.address}:`, error);
          EnhancedErrorLogger.getInstance().logQuickError(error, 'BoundaryLookup', { context: 'batch address processing' });
        }
      });

      await Promise.all(batchPromises);
      
      // Rate limiting: small delay between batches
      if (i + batchSize < uncachedAddresses.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return results;
  }

  /**
   * Start background cache refresh process
   */
  public startBackgroundRefresh(): void {
    if (this.refreshTimer) return;
    // Clear expired entries every hour
    this.refreshTimer = setInterval(() => {
      void this.clearExpiredCache();
    }, 60 * 60 * 1000); // 1 hour
  }

  /**
   * Stop background refresh process
   */
  public stopBackgroundRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

export default BoundaryLookupService;